import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../config/axiosInstance";
import AddUserModal from "./AddUserModal";

const Staff = () => {
  const [loading, setLoading] = useState(false);
  const [staffData, setStaffData] = useState([]);
  const [stats, setStats] = useState([]);
  const [roleFilter, setRoleFilter] = useState("");
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchStats = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/api/users/stats");
      const { total, roles } = res.data;
      setStats([
        { label: "Total", value: total, trend: "", up: true },
        { label: "Reception", value: roles.receptionist, trend: "", up: true },
        { label: "Master", value: roles.master, trend: "", up: true },
      ]);
    } catch (err) {
      toast({ status: "error", title: "Failed to load stats" });
    }
  }, [toast]);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `/api/users${roleFilter ? `?role=${roleFilter}` : ""}`
      );
      setStaffData(res.data);
    } catch (err) {
      toast({ status: "error", title: "Failed to fetch staff" });
    } finally {
      setLoading(false);
    }
  }, [roleFilter, toast]);

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/api/users/${id}`);
      toast({ status: "success", title: "User deleted" });
      fetchStaff();
      fetchStats();
    } catch (err) {
      toast({ status: "error", title: "Delete failed" });
    }
  };

  const updateRole = async (id, newRole) => {
    try {
      setLoading(true);
      await axiosInstance.patch(`/api/users/${id}/role`, { role: newRole });
      toast({ title: "Role updated", status: "success" });
      fetchStaff();
    } catch (error) {
      toast({ title: "Update failed", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchStaff();
  }, [fetchStats, fetchStaff]);

  return (
    <Box p={{ base: 4, md: 6 }} bg="gray.50" minH="100vh">
      <Flex
        justify="space-between"
        align={{ base: "start", md: "center" }}
        wrap="wrap"
        gap={4}
        mb={6}
      >
        <Text fontSize="2xl" fontWeight="bold">
          Staff
        </Text>
      </Flex>

      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6} mb={6}>
        {stats.map((stat, index) => (
          <Box
            key={index}
            bg="white"
            p={4}
            borderLeft="4px solid"
            borderColor={stat.up ? "green.400" : "red.400"}
            rounded="md"
            shadow="sm"
          >
            <Text fontSize="sm" color="gray.600">
              {stat.label}
            </Text>
            <Text fontSize="2xl" fontWeight="bold">
              {stat.value}
            </Text>
            {stat.trend && (
              <Text fontSize="sm" color={stat.up ? "green.500" : "red.500"}>
                {stat.trend}
              </Text>
            )}
          </Box>
        ))}
      </SimpleGrid>

      <Divider mb={4} />

      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
        <Text fontWeight="semibold">All Staff Members</Text>
        <HStack spacing={2} align="center">
          <Select
            size="sm"
            placeholder="Filter by Role"
            onChange={(e) => setRoleFilter(e.target.value)}
            w="180px"
            height="32px"
            value={roleFilter}
          >
            <option value="receptionist">Receptionist</option>
            <option value="master">Master</option>
          </Select>
          <Button
            leftIcon={<AddIcon />}
            size="sm"
            variant="outline"
            onClick={onOpen}
            height="32px"
          >
            Add New
          </Button>
        </HStack>
      </Flex>

      <Box bg="white" borderRadius="md" shadow="sm" p={4} overflowX="auto">
        <Table size="sm" minW="1000px">
          <Thead>
            <Tr>
              <Th>S.No</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Role</Th>
              <Th>Present</Th>
              <Th>Update Role</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {staffData.map((staff, index) => (
              <Tr key={staff._id}>
                <Td>{index + 1}</Td>
                <Td>{staff.name}</Td>
                <Td>{staff.email}</Td>
                <Td>{staff.phone}</Td>
                <Td textTransform="capitalize">{staff.role}</Td>
                <Td>
                  <Switch isChecked={staff.isActive} colorScheme="green" isReadOnly />
                </Td>
                <Td>
                  <Select
                    size="xs"
                    value={staff.role}
                    onChange={(e) => updateRole(staff._id, e.target.value)}
                    isDisabled={loading}
                  >
                    <option value="admin">Admin</option>
                    <option value="receptionist">Receptionist</option>
                    <option value="master">Master</option>
                  </Select>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <IconButton
                      icon={<DeleteIcon />}
                      size="xs"
                      aria-label="Delete"
                      onClick={() => handleDelete(staff._id)}
                      isDisabled={loading}
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <AddUserModal isOpen={isOpen} onClose={onClose} fetchUsers={fetchStaff} />
    </Box>
  );
};

export default Staff;
